import { CSSProperties, FC } from 'react'

import cn from 'classnames'
import { motion } from 'motion/react'

import { getItemBorderRadius } from '../../utils/getItemBorderRadius'
import { getItemContentScale } from '../../utils/getItemContentScale'
import { gridItemMarker } from '../../utils/gridItemDom'
import styles from './BaseItem.module.scss'
import { BaseItemProps } from './BaseItem.types'

export const BaseItem: FC<BaseItemProps> = ({
  itemMetaData,
  innerClassName,
  cellSize,
  onDragStart,
  onDragEnd,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  className,
  onClick,
  onDoubleClick,
  onKeyDown,
  ariaLabel,
  ariaBusy,
  children,
  disabled,
  suppressClick,
  isTouchReorderActive,
  disableNativeDrag,
  disableContextMenu
}) => {
  const gridPlacement = {
    gridColumnStart: itemMetaData.position.col + 1,
    gridRowStart: itemMetaData.position.row + 1,
    gridColumnEnd: `span ${itemMetaData.size.width}`,
    gridRowEnd: `span ${itemMetaData.size.height}`
  }

  const animatedWidth =
    cellSize.width * itemMetaData.size.width +
    cellSize.gapX * (itemMetaData.size.width - 1)
  const animatedHeight =
    cellSize.height * itemMetaData.size.height +
    cellSize.gapY * (itemMetaData.size.height - 1)

  const borderRadius = getItemBorderRadius(cellSize.width)
  const contentScale = getItemContentScale(cellSize.width)

  return (
    <motion.div
      {...gridItemMarker(itemMetaData.id)}
      className={cn(styles.container, className, {
        [styles.disabled]: disabled
      })}
      style={
        {
          ...gridPlacement,
          '--dg-item-border-radius': `${borderRadius}px`,
          '--dg-item-content-scale': contentScale
        } as CSSProperties
      }
      animate={{ width: animatedWidth, height: animatedHeight }}
      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
      layout={false}
    >
      <div
        data-desktop-internal-drag-source
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        aria-busy={ariaBusy || undefined}
        draggable={!disableNativeDrag && !disabled && !isTouchReorderActive}
        onDragStart={onDragStart(itemMetaData.id)}
        onDragEnd={onDragEnd}
        className={cn(styles.dragArea, innerClassName)}
        onClick={e => {
          if (suppressClick) {
            e.preventDefault()
            e.stopPropagation()
            return
          }
          onClick?.(e)
        }}
        onDoubleClick={onDoubleClick}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown?.(itemMetaData.id)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onContextMenu={e => {
          if (disableContextMenu) {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      >
        {children}
      </div>
    </motion.div>
  )
}
